import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  Clock,
  IndianRupee,
  Stethoscope,
  Video,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

function KpiCard({ label, value, icon: Icon, accent }) {
  const testid = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return (
    <div
      data-testid={`kpi-${testid}`}
      className="bg-white border border-border rounded-2xl p-5 shadow-sm flex items-center gap-4"
    >
      <span
        className={
          "h-11 w-11 rounded-xl flex items-center justify-center " +
          (accent || "bg-accent text-accent-foreground")
        }
      >
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="text-2xl font-semibold tracking-tight mt-0.5">{value}</div>
      </div>
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Kolkata",
  });
}

export default function DoctorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/doctor/me/dashboard");
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="min-h-screen bg-card text-foreground flex flex-col">
      <Navbar />
      <main
        data-testid="doctor-dashboard"
        className="flex-1 px-6 lg:px-10 py-8 lg:py-10 max-w-7xl w-full mx-auto space-y-8"
      >
        <section className="bg-primary text-primary-foreground rounded-3xl p-7 sm:p-9 relative overflow-hidden shadow-sm">
          <div className="absolute -right-12 -top-12 h-56 w-56 rounded-full bg-white/10 blur-2xl" aria-hidden />
          <div className="relative">
            <div className="text-xs font-bold uppercase tracking-[0.2em] opacity-80 mb-3">
              Doctor · Healthink
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Welcome back, {user?.name?.split(" ")[1] || user?.name || "Doctor"}.
            </h1>
            <p className="mt-2 text-sm sm:text-base opacity-90">
              {new Date().toLocaleDateString("en-IN", {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
              })}{" "}
              · Have a great practice day.
            </p>
          </div>
        </section>

        {/* KPI cards */}
        {loading || !data ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 bg-muted rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard label="Today" value={data.today_appointments_count} icon={Calendar} />
            <KpiCard label="Upcoming" value={data.upcoming_count} icon={Clock} />
            <KpiCard
              label="Completed (Month)"
              value={data.completed_this_month}
              icon={CheckCircle2}
              accent="bg-emerald-50 text-emerald-700"
            />
            <KpiCard
              label="Earnings (Month)"
              value={`₹${data.earnings_this_month.toLocaleString("en-IN")}`}
              icon={IndianRupee}
              accent="bg-amber-50 text-amber-700"
            />
          </div>
        )}

        {/* Today's schedule */}
        <section className="space-y-4">
          <div className="flex items-end justify-between">
            <div>
              <h2 className="text-xl font-semibold tracking-tight">Today's schedule</h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                Consults scheduled for today, in order.
              </p>
            </div>
            <Button
              variant="outline"
              data-testid="doctor-dashboard-view-all-appts"
              onClick={() => navigate("/doctor/appointments")}
              className="rounded-full"
            >
              All appointments <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>

          {loading ? (
            <div className="h-32 bg-muted rounded-2xl animate-pulse" />
          ) : (data?.todays_schedule || []).length === 0 ? (
            <div
              data-testid="doctor-dashboard-empty"
              className="bg-white border border-border rounded-2xl p-10 text-center"
            >
              <Calendar className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <div className="font-semibold text-lg mb-1">No appointments today</div>
              <div className="text-sm text-muted-foreground">
                Enjoy the breather. Patients can still book future slots.
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {data.todays_schedule.map((a) => (
                <div
                  key={a.id}
                  data-testid={`doctor-today-row-${a.id}`}
                  className="bg-white border border-border rounded-2xl p-4 flex items-center gap-4"
                >
                  <div className="text-center w-16 shrink-0">
                    <div className="text-xs text-muted-foreground uppercase">Time</div>
                    <div className="font-semibold">{fmtTime(a.scheduled_at)}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{a.patient?.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{a.patient?.phone}</div>
                    <div className="flex gap-1.5 mt-1">
                      <span className="text-[11px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground inline-flex items-center gap-1">
                        {a.mode === "video" ? <Video className="h-3 w-3" /> : <Stethoscope className="h-3 w-3" />}
                        {a.mode}
                      </span>
                      <span className="text-[11px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-accent text-accent-foreground">
                        {a.status}
                      </span>
                    </div>
                  </div>
                  <Button
                    data-testid={`doctor-today-start-${a.id}`}
                    onClick={() => navigate(`/doctor/consult/${a.id}`)}
                    className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground"
                  >
                    {a.status === "completed" ? "View" : a.mode === "video" ? "Start consult" : "Start OPD"}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
